/**
 * Constants used throughout the Financial Close module
 */

/**
 * Bulk submit column headers for SL Breakup Excel export.
 * Keyed by cycle type ('Actual' | 'Forecast').
 * Last 2 entries ('Local Approve & Submit' columns) are stripped before download.
 */
export const SL_BULK_SUBMIT_COLUMNS = {
  Actual: [
    'Client Code', 'Description', 'Actual Amount', 'Proforma Amount',
    'Variance Amt(Act vs Pro)', 'Variance %(Act vs Pro)',
    'Cross Local Ctrl', 'Cross Local Mngt', 'Cross Regional Ctrl', 'Cross Regional Mngt',
    'Local Act vs Pro Commentary', 'Regional Act vs Pro Commentary',
    'Forecast Amount', 'Variance Amt(Act vs For)', 'Variance %(Act vs For)',
    'Cross Local Ctrl', 'Cross Local Mngt', 'Cross Regional Ctrl', 'Cross Regional Mngt',
    'Local Act vs For Commentary', 'Regional Act vs For Commentary',
    'Status Local', 'Status Regional',
    'Local Approve/Reject (Yes/No)', 'Local Approve/Reject Reason',
    'Regional Approve/Reject (Yes/No)', 'Regional Approve/Reject Reason',
    'Local Approve & Submit (Yes/No)', 'Local Approve & Submit Reason',
  ],
  Forecast: [
    'Client Code', 'Description', 'Forecast Amount', 'Proforma Amount',
    'Variance Amt(For vs Pro)', 'Variance %(For vs Pro)',
    'Cross Local Ctrl', 'Cross Local Mngt', 'Cross Regional Ctrl', 'Cross Regional Mngt',
    'Local For vs Pro Commentary', 'Regional For vs Pro Commentary',
    'Previous Forecast Amount', 'Variance Amt(For vs Prev For)', 'Variance %(For vs Prev For)',
    'Cross Local Ctrl', 'Cross Local Mngt', 'Cross Regional Ctrl', 'Cross Regional Mngt',
    'Local For vs Prev For Commentary', 'Regional For vs Prev For Commentary',
    'Status Local', 'Status Regional',
    'Local Approve/Reject (Yes/No)', 'Local Approve/Reject Reason',
    'Regional Approve/Reject (Yes/No)', 'Regional Approve/Reject Reason',
    'Local Approve & Submit (Yes/No)', 'Local Approve & Submit Reason',
  ],
};

/**
 * Pagination constants for reports
 */
export const PAGINATION = {
  DEFAULT_PAGE: 0,
  DEFAULT_LIMIT: 10,
};

/**
 * Materiality type display labels
 */
export const MATERIALITY_TYPE_LABELS = {
  sl: 'Drilldown',
  ru: 'RU',
  gl: 'GL',
};

/**
 * Cycle type display labels
 */
export const CYCLE_TYPE_LABELS = {
  actual: 'Actual',
  forecast: 'Forecast',
};

/**
 * Module display labels
 */
export const MODULE_LABELS = {
  pl: 'Profit & Loss',
  bs: 'Balance Sheet',
  all: 'All',
};

/**
 * Month names array
 */
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const STATEMENT_TYPES = {
  BALANCE_SHEET: 'bs',
  PROFIT_AND_LOSS: 'pl',
};

/**
 * Workflow constants
 */
export const TOTAL_WORKFLOW_COUNT = 8;

/**
 * View modes for Variance Analysis
 */
export const VIEW_MODES = {
  RU: 0,
  CONSOLIDATED: 3,
};

export const STATEMENT_TAB_INDEX = {
  BALANCE_SHEET: 0,
  PROFIT_AND_LOSS: 1,
};

export const STATEMENT_TABS = [
  { label: 'Balance Sheet', value: STATEMENT_TYPES.BALANCE_SHEET, index: STATEMENT_TAB_INDEX.BALANCE_SHEET },
  { label: 'Profit & Loss', value: STATEMENT_TYPES.PROFIT_AND_LOSS, index: STATEMENT_TAB_INDEX.PROFIT_AND_LOSS },
];

export const GL_SELECTION_HEADERS = [
  "Navigation", 
  "GL Code & Description",
  "GL Selection", 
  "GL Status", 
  "Drill-Down Selection", 
  "Drill-Down Status", 
  "GL(s) & Drill-Down Count"
];

export const PENDING_REQUEST_HEADERS = [
  {
    value: "indexno",
    text: "S.No",
    sortable: false,
    filterable: false,
    alignment: "text-center"
  },
  {
    value: "typetext",
    text: "Request Type",
    sortable: false,
    filterable: false,
    datatype: "string",
    lock: true,
    visible: false,
  },
  {
    type: "string",
    value: "submittername",
    text: "Submitted By",
    sortable: false,
    filterable: true,
  },
  {
    type: "date",
    value: "created_at",
    text: "Submitted On",
    sortable: true,
    defaultsort: 'desc'
  },
  {
    value: "action",
    text: "Action",
    sortable: false,
  },
];

/**
 * Constants for RU Info Dialog component
 */
export const RU_INFO_DIALOG = {
  DEFAULT_RU_TYPE: '',
  ERROR_MESSAGE: 'Failed to retrieve reporting unit insights. Please try again.',
  TABLE_HEADERS: [
    { text: 'RU Code & Description', value: 'ru_code_description', sortable: false, filterable: true },
    { text: 'Network', value: 'network', sortable: false, filterable: true },
    { text: 'Region', value: 'region', sortable: false, filterable: true },
    { text: 'Country', value: 'country', sortable: false, filterable: true },
  ],
};

export function getEmailNotificationTableHeadersForSchedulersType() {
  return [
    {
      value: "indexno",
      text: "S.No",
      sortable: false,
      filterable: false,
    },
    {
      value: "labelName",
      text: "Features",
      sortable: false,
      filterable: false,
      lock: true,
    },
    {
      value: "periodCount",
      text: "Coverage Months",
      sortable: false,
      filterable: true,
      lock: true,
    },
    {
      value: "scheduler",
      text: "Scheduled At",
      sortable: false,
      filterable: false,
      lock: true,
    },
    {
      value: "statusvalue",
      text: "Status",
      sortable: false,
      filterable: true,
      lock: true,
    },
    {
      value: "action",
      text: "Action",
      sortable: false,
      filterable: false,
      lock: true,
      alignment: "center",
    },
  ];
}

export function getEmailNotificationCoverageMonths() {
  return [
    {
      value: 1,
      name: "Last 1 month",
    },
    {
      value: 2,
      name: "Last 2 months",
    },
    {
      value: 3,
      name: "Last 3 months",
    },
  ];
}

export function getEmailNotificationTableHeadersForEventTypes() {
  return [
    { value: "indexno", text: "S.No", sortable: false, filterable: false, width: 50 },
    {
      value: "labelName",
      text: "Feature",
      sortable: false,
      filterable: true,  
      lock: true,
      width: 728
    },
    {
      value: "statusvalue",
      text: "Status",
      sortable: false,
      filterable: true,
      lock: true,
      width: 248,
    },
    {
      value: "action",
      text: "Action",
      sortable: false,
      filterable: false,
      lock: true,
      width: 96,
    },
  ];
}

export const NOTIFICATIONS = {
  NOTIFICATION_TYPE: {
    SCHEDULERS: "scheduler",
    REALTIME: "realTime",
  },
  requiredFields: {
    scheduler: ["status", "feature", "periodCount"],
    realTime: ["feature"],
  },
  TYPE: "commentary",
  FEATURE:"feature",
  STATUS: {
    ENABLED: "Enabled",
    DISABLED: "Disabled",
  },
  MESSAGES: {
    SUCCESS_ADD: "Notification added successfully",
    SUCCESS_UPDATE: "Notification updated successfully!",
    SUCCESS_STATUS_CHANGE: "Notification status changed successfully!",
    SUCCESS_ENABLED: "Email notification enabled successfully",
    SUCCESS_DISABLED: "Email notification disabled successfully",
    SUCCESS_GET: "Notifications fetched successfully!",
    ERROR_ADD: "Failed to add notification",
    ERROR_UPDATE: "Failed to update notification",
    ERROR_STATUS_CHANGE: "Failed to change notification status",
    NO_FEATURE_AVAILABLE: "No features available. All available notifications are created",
    ERROR_GET: "Failed to fetch notifications",
    ERROR_SELECT_FEATURE: "Please select a feature.",
    ERROR_REQUIRED_FIELDS: "Please fill the required fields to enable a notification.",
  },
  ENABLED_SLOTS: {
    SCHEDULER: ["scheduler", "action", "statusvalue", "enabledordisabled"],
    REALTIME: ["action", "statusvalue"],
  },
  LOADING_SKELETON_TYPES: {
    TABLE: "table-heading, list-item-three-line, article",
  },
  API_ENDPOINTS: {
    GET_ALL_FEATURES_BY_CLIENT: "/v2/varianceanalysis/analysis/getAllFeatures",
    GET_ALL_AVAILABLE_FEATURES:
      "/v2/varianceanalysis/analysis/getAllAvailableFeatures",
    CREATE_OR_UPDATE_NOTIFICATION:
      "/v2/varianceanalysis/approvals/updateOrCreateSchedulerFeature",
    CREATE_REAL_TIME_NOTIFICATIONS: "/v2/varianceanalysis/approvals/createRealTimeNotifications"
  },
};

export const SUPPORTED_FILE_FORMAT = ["xlsx", "xls", "xlsm", "xlsb"];

export const MESSAGES = {
  INVALID_FILE_FORMAT:"Only xlsx, xls, xlsm, xlsb file extension supported"
}

export const VARIANCE_ANALYSIS_MESSAGES = {
  SL_DATA: {
    CACHE_SUCCESS: 'Revenue breakup context cached.',
    CACHE_ERROR: 'Unable to prepare revenue breakup context.',
    CLEAR_SUCCESS: 'Revenue breakup context cleared.',
    CLEAR_ERROR: 'Failed to clear revenue breakup context.'
  }
};

export const SL_CONTEXT_STORAGE_KEY = 'varianceanalysis.slContext';
export const BASE_PAGE_STATE_STORAGE_KEY = 'varianceanalysis.basePageState';
export const ACTUAL_FILTER_STORAGE_KEY = 'varianceanalysis.actualFilter';
export const FORECAST_FILTER_STORAGE_KEY = 'varianceanalysis.forecastFilter';
export const ACTUAL_HIERARCHY_STORAGE_KEY = 'varianceanalysis.actualHierarchy';
export const FORECAST_HIERARCHY_STORAGE_KEY = 'varianceanalysis.forecastHierarchy';

/**
 * Filter Constants
 * Used by Filter.vue component for module types, roles, and default selections
 */
export const FILTER_MODULE_TYPES = {
  ALL: { _id: 'all', name: 'All' },
  PROFIT_AND_LOSS: { _id: 'pl', name: 'Profit & Loss' },
  BALANCE_SHEET: { _id: 'bs', name: 'Balance Sheet' },
};

export const FILTER_MODULE_LIST = Object.fromEntries(
  Object.values(FILTER_MODULE_TYPES).map(m => [m._id, m])
);

export const FILTER_MODULES_ARRAY = Object.values(FILTER_MODULE_TYPES);

export const FILTER_ROLES = {
  ALL: { _id: 'all', name: 'All' },
  TOOL_ADMIN_PREPARER: { _id: 'tap', name: 'Tool Admin Preparer' },
  TOOL_ADMIN_APPROVER: { _id: 'taa', name: 'Tool Admin Approver' },
  SUPER_ADMIN: { _id: 'sa', name: 'Super Admin' },
  LOCAL_PREPARER: { _id: 'local_preparer', name: 'Local Preparer' },
  REGIONAL_PREPARER: { _id: 'regional_preparer', name: 'Regional Preparer' },
  LOCAL_APPROVER: { _id: 'local_approver', name: 'Local Approver' },
  REGIONAL_APPROVER: { _id: 'regional_approver', name: 'Regional Approver' },
  MANAGER: { _id: 'manager', name: 'Manager' },
  GENERAL_USER: { _id: 'general_user', name: 'General User' },
  VIEW_ONLY_USER: { _id: 'view_only_user', name: 'View Only' },
};

export const FILTER_ROLE_LIST = Object.fromEntries(
  Object.values(FILTER_ROLES).map(r => [r._id, r])
);

export const FILTER_ROLES_ARRAY = Object.values(FILTER_ROLES);

export const FILTER_MATERIALITY_TYPES = {
  // ALL: { _id: 'all', name: 'All' },
  RU: { _id: 'ru', name: 'RU' },
  DRILLDOWN: { _id: 'sl', name: 'Drill-down' },
  GL: { _id: 'gl', name: 'GL' },
};

export const FILTER_MATERIALITY_LIST = Object.fromEntries(
  Object.values(FILTER_MATERIALITY_TYPES).map(m => [m._id, m])
);

export const FILTER_MATERIALITY_ARRAY = Object.values(FILTER_MATERIALITY_TYPES);

export const FILTER_CYCLE_TYPES = {
  // ALL: { _id: 'all', name: 'All' },
  ACTUAL: { _id: 'actual', name: 'Actual' },
  FORECAST: { _id: 'forecast', name: 'Forecast' },
};

export const FILTER_CYCLE_LIST = Object.fromEntries(
  Object.values(FILTER_CYCLE_TYPES).map(c => [c._id, c])
);

export const FILTER_CYCLE_ARRAY = Object.values(FILTER_CYCLE_TYPES);

export const FILTER_REQUEST_TYPES = {
  ALL: { _id: 'all', name: 'All' },
  
  
  GL_SELECTION_CHANGE: { _id: 'glselectionchange', name: 'GL Selection Change' },
  BULK_RU_ACCESS_CHANGE: { _id: 'bulkaccesschange', name: 'Bulk RU Access Change' },
  RU_ACCESS_CHANGE: { _id: 'accesschange', name: 'RU Access Change' },
  FILE_RU_ACCESS_CHANGE: { _id: 'fileaccesschange', name: 'File RU Access Change' },
 

  RU_LEVEL_MATERIALITY_FILE_UPLOAD: { _id: 'filematerialityrulevel', name: 'RU Level Materiality File Upload' },
  RU_LEVEL_MATERIALITY: { _id: 'materialityrulevel', name: 'RU Level Materiality' },
  BULK_RU_LEVEL_MATERIALITY: { _id: 'bulkmaterialityrulevel', name: 'Bulk RU Level Materiality' },

  GL_LEVEL_MATERIALITY_FILE_UPLOAD: { _id: 'filematerialitygllevel', name: 'GL Level Materiality File Upload' },
  GL_LEVEL_MATERIALITY: { _id: 'materialitygllevel', name: 'GL Level Materiality' },
  BULK_GL_LEVEL_MATERIALITY: { _id: 'bulkmaterialitygllevel', name: 'Bulk GL Level Materiality' },

  DRILL_DOWN_LEVEL_MATERIALITY: { _id: 'materialitysllevel', name: 'Drill-Down Level Materiality' },
  BULK_DRILL_DOWN_LEVEL_MATERIALITY: { _id: 'bulkmaterialitysllevel', name: 'Bulk Drill-Down Level Materiality' },
  DRILL_DOWN_LEVEL_MATERIALITY_FILE_UPLOAD: { _id: 'filematerialitysllevel', name: 'Drill-Down Level Materiality File Upload' },
};

export const FILTER_REQUEST_TYPE_LIST = Object.fromEntries(
  Object.values(FILTER_REQUEST_TYPES).map(r => [r._id, r])
);

export const FILTER_REQUEST_TYPES_ARRAY = Object.values(FILTER_REQUEST_TYPES);

/**
 * Approval Status filter options
 */
export const FILTER_APPROVAL_STATUS = {
  ALL: { _id: 'all', name: 'All' },
  OPEN: { _id: 0, name: 'Open Request' },
  IN_PROGRESS: { _id: 2, name: 'In Progress' },
  APPROVED: { _id: 1, name: 'Approved Requests' },
  REJECTED: { _id: -1, name: 'Rejected Requests' },
  FAILED: { _id: 3, name: 'Failed Requests' },
};

export const FILTER_APPROVAL_STATUS_LIST = Object.fromEntries(
  Object.values(FILTER_APPROVAL_STATUS).map(s => [s._id, s])
);

export const FILTER_APPROVAL_STATUS_ARRAY = Object.values(FILTER_APPROVAL_STATUS);

/**
 * Batch Type filter options for Data Centre Report
 */
export const FILTER_BATCH_TYPES = {
  ALL: { _id: 'all', name: 'All' },
  GL_DATA: { _id: 'gl_data', name: 'GL Data' },
  DRILL_DOWN: { _id: 'drill_down', name: 'Drill-down' },
  MASTER: { _id: 'master', name: 'Master' },
};

export const FILTER_BATCH_TYPE_LIST = Object.fromEntries(
  Object.values(FILTER_BATCH_TYPES).map(b => [b._id, b])
);

export const FILTER_BATCH_TYPES_ARRAY = Object.values(FILTER_BATCH_TYPES);

/**
 * Data Centre Status filter options
 */
export const FILTER_DATA_CENTRE_STATUS = {
  ALL: { _id: 'all', name: 'All' },
  IN_PROGRESS: { _id: 1, name: 'In Progress' },
  COMPLETED: { _id: 2, name: 'Complete' },
  FAILED: { _id: -1, name: 'Failed' },
};

export const FILTER_DATA_CENTRE_STATUS_LIST = Object.fromEntries(
  Object.values(FILTER_DATA_CENTRE_STATUS).map(s => [s._id, s])
);

export const FILTER_DATA_CENTRE_STATUS_ARRAY = Object.values(FILTER_DATA_CENTRE_STATUS);

export const FILTER_DEFAULTS = {
  SELECTED_MODULE: 'all',
  SELECTED_ROLE: 'all',
  SELECTED_NETWORK: 'all',
  SELECTED_REGION: 'all',
  SELECTED_COUNTRY: 'all',
  SELECTED_REPORTING_UNIT: 'all',
  SELECTED_RU_TYPE: 'all',
  // Materiality and Cycle Type intentionally have no "All" option.
  SELECTED_MATERIALITY: 'ru',
  SELECTED_CYCLETYPE: 'actual',
  SELECTED_REQUEST_TYPE: 'all',
  SELECTED_APPROVAL_STATUS: 'all',
};

export const SL_ANALYSIS_API_BASE = '/v3/varianceanalysis/sl_analysis';
export const ANALYSIS_API_BASE = '/v3/varianceanalysis/analysis';


export const COMMENTARY_AI = {
  ENDPOINTS: {
    REVENUE: '/v2/ai/generate-commentary-revenue',
    NET_SALES: '/v2/ai/generate-commentary-netsales',
    SUMMARY: '/v2/ai/generate-commentary-summary',
    LOCAL: '/v2/ai/generate-commentary',
  },
  VARIANTS: {
    PROFORMA: 'proforma',
    FORECAST: 'forecast',
  },
  MESSAGES: {
    THRESHOLD_BLOCKED: 'Threshold exceeded. Gen AI summarisation is disabled.',
    MISSING_CONTEXT: 'No GL code selected for AI generation.',
    MISSING_CYCLE: 'No cycle context available for AI generation.',
    EMPTY_RESPONSE: 'AI response is empty or invalid.',
    GENERATION_SUCCESS: 'AI commentary generated successfully.',
    GENERATION_ERROR: 'Failed to generate AI commentary.',
  },
};

/**
 * Resolve proforma vs forecast from Gen AI emit payload.
 * CommentaryMainTab passes slot as 'One'|'Two' and mode as 'proforma'|'forecast'.
 */
export function normalizeCommentaryAiVariant(slot = '', mode = '') {
  const modeLower = String(mode).toLowerCase();
  if (modeLower === COMMENTARY_AI.VARIANTS.FORECAST || modeLower === COMMENTARY_AI.VARIANTS.PROFORMA) {
    return modeLower;
  }

  const slotLower = String(slot).toLowerCase();
  if (slotLower === 'two' || slotLower === COMMENTARY_AI.VARIANTS.FORECAST) {
    return COMMENTARY_AI.VARIANTS.FORECAST;
  }
  if (slotLower === 'one' || slotLower === COMMENTARY_AI.VARIANTS.PROFORMA) {
    return COMMENTARY_AI.VARIANTS.PROFORMA;
  }

  return COMMENTARY_AI.VARIANTS.PROFORMA;
}

export function commentaryAiTypeToken(variant = '') {
  return String(variant).toLowerCase() === COMMENTARY_AI.VARIANTS.FORECAST ? 'Two' : 'One';
}

export const SL_COMMENTARY = {
  ENDPOINTS: {
    APPROVE_SINGLE: '/v3/varianceanalysis/analysis/approvecommentary',
    APPROVE_BULK: '/v3/varianceanalysis/analysis/approvebulkcommentary',
    REJECT_SINGLE: '/v3/varianceanalysis/analysis/rejectcommentary',
    REJECT_BULK: '/v3/varianceanalysis/analysis/rejectbulkcommentary',
    SAVE_SINGLE: '/v3/varianceanalysis/analysis/addcommentary',
    SAVE_BULK: '/v3/varianceanalysis/analysis/addbulkcommentary',
    SAVE_APPROVAL_REASON: '/v3/varianceanalysis/analysis/saveapprovalreason',
  },
  MESSAGES: {
    APPROVE_SUCCESS: 'Commentary approved successfully',
    APPROVE_ERROR: 'Error approving commentary',
    BULK_APPROVE_SUCCESS: 'Bulk commentary approved',
    REJECT_SUCCESS: 'Commentary rejected',
    REJECT_INFO: 'Commentary rejected',
    BULK_REJECT_INFO: 'Bulk commentary rejected',
    REJECT_ERROR: 'Error rejecting commentary',
    SAVE_SUCCESS: 'Commentary saved successfully',
    SAVE_ERROR: 'Error saving commentary',
    SAVE_REASON_SUCCESS: 'Reason saved successfully.',
    SAVE_REASON_ERROR: 'Unable to save the reason. Please try again.',
    SAVE_REASON_VALIDATION: 'Please enter a reason before saving.',
    ADD_SUCCESS: 'Commentary added successfully',
    ADD_ERROR: 'Error adding commentary',
  },
  ACTION_TYPES: {
    APPROVE: 'approve',
    REJECT: 'reject',
    SAVE: 'save',
    SAVE_REASON: 'save-reason',
    ADD: 'add',
  },
};

export const BULK_APPROVE_REJECT = {
  ACTIONS: ['yes', 'y', 'no', 'n'],
  REJECTION_TARGETS: ['la', 'lp'],
  REJECTION_TARGET_MAP: {
    'la': 'local_approver',
    'lp': 'local_preparer'
  },
  MESSAGES:{
    GL_NOT_IN_AWAITING_APPROVAL_STATUS:"GL is not in pending approval status",
    ACTION_MISSING:"Approve/Reject action is missing",
    INVALID_ACTION:"Invalid action. Only 'Yes' or 'No' allowed",
    REJECTION_TARGET_REQUIRED:"Rejection target is required when rejecting. Please provide 'LA' or 'LP'",
    INVALID_REJECTION_TARGET:"Rejection target must be 'LA' or 'LP' only",
    APPROVAL_REASON_REQUIRED:"Approval reason is required for control threshold-crossing GLs",
    CONTROL_THRESHOLD_EXCEEDED:"Bulk Approval has been disabled: control threshold exceeded",
    MANAGEMENT_THRESHOLD_EXCEEDED:"Bulk Approval has been disabled: management threshold exceeded",
    UNKNOWN_GL:"Unknown GL",
    CANNOT_PROCESS_GL:"Cannot process this GL",
    GL_NOT_FOUND:"GL Code not found",
    READY_TO_UPLOAD:"Ready to upload",
    BULK_APPROVE_NOT_ALLOWED:" - bulk approval not allowed"
  },
  GL_STATUS:{
    REJECTED:-1,
    APPROVED:2,
    AWAITING_COMMENTARY:0,
    AWAITING_APPROVAL:1
  },
  APPROVE_TYPES:{
    APPROVE:"Approve",
    APPROVE_AND_SUBMIT:"Approve and Submit",
  }

}
export const ACCESS_CHANGE = {
  TYPE:{
    SINGLE:"single",
    BULK:"bulk",
    FILE:"file"
  },
  REQUEST_TYPE:{
    ACCESS_CHANGE:"accesschange",
    BULK_ACCESS_CHANGE:"bulkaccesschange",
    FILE_ACCESS_CHANGE:"fileaccesschange"
  },
  ACTION:{
    REMOVE:"remove",
    ADD:"add"
  },
  ROLE:{
    LOCAL_PREPARER_IDS:"l_preparer_ids",
    LOCAL_APPROVER_IDS:"l_approver_ids",
    REGIONAL_APPROVER_IDS:"r_approver_ids",
    MANAGER_IDS:"manager_ids"
  }
}

export const JV_COMMENTARY = {
  ACTUAL: 'commentary1',
  FORECAST: 'commentary2',
  REGIONAL_ACTUAL: 'regionalcommentary1',
  REGIONAL_FORECAST: 'regionalcommentary2'
}

export const STAGES = {
  STAGE_LOCAL: '__stage',
  STAGE_REGIONAL: '__stageregional',
  COMMENTARY_AWAITING: 0,
  COMMENTARY_NIL_VALUE: 3,
  IMMATERIAL_VARIANCE: 4,
  COMMENTARY_APPROVED: 7,
  COMMENTARY_NOT_IN_SCOPE: 9
}

/**
 * Stages eligible for request commentary.
 * 3 = Nil Value, 4 = Immaterial Variance, 7 = Approved Commentary, 8 = Regional Approved, 9 = Not In Scope
 */
export const REQUEST_COMMENTARY_ELIGIBLE_STAGES = [3, 4, 7, 8, 9];

export const WORKFLOWS_STATUS = {
  IS_LOCAL_ENABLED: 'islocalenabled',
  IS_REGIONAL_ENABLED: 'isregionalenabled'
}

export const ROLE_OPTIONS = [
    { value: 'preparer',          label: 'Local Preparer',         color: 'info',    priority: 1 },
    { value: 'approver',          label: 'Local Approver',    color: 'warning', priority: 2 },
    { value: 'regionalapprover',  label: 'Regional Approver', color: 'success', priority: 3 },
    { value: 'manager',           label: 'Manager',           color: 'grey',    priority: 4 },
  ];

export const ROLES = {
  LOCAL_PREPARER:"preparer",
  LOCAL_APPROVER:"approver",
  REGIONAL_APPROVER:"regionalapprover",
  MANAGER:"manager"
}

 export const ROLE_LABEL_MAPPING = {
    [ROLES.LOCAL_PREPARER]: {
      key: 'commentary_local_preparer_label',
      default: 'Local Preparer'
    },
    [ROLES.LOCAL_APPROVER]: {
      key: 'commentary_local_approver_label', 
      default: 'Local Approver'
    },
    [ROLES.REGIONAL_APPROVER]: {
      key: 'commentary_regional_approver_label',
      default: 'Regional Approver'
    },
    [ROLES.MANAGER]: {
      key: 'commentary_manager_label',
      default: 'Manager'
    }
  };

  export const VIEW_ONLY = {
    key:"view_only",
    default:"View Only"
  }

  export const UPDATE_RU_SETTINGS = {
    TYPES:{
      WORKFLOW:"workflow",
      BULK_APPROVE:"bulkApprove",
      AUTO_SUBMISSION:"autoSubmission",
      SOD:"sod"
    },
    MUTATORS:{
      WORKFLOW:"SET_REPORTING_UNIT_WORKFLOW_SETTINGS",
      OTHER_SETTINGS:"SET_REPORTING_UNIT_OTHER_SETTINGS",
    },
    DISPATCHERS:{
      UPDATE_BULK_APPROVE:"updateBulkApproveSettings",
      UPDATE_BULK_WORKFLOW:"updateBulkWorkflowSettings",
      UPDATE_AUTO_SUBMISSION:"updateAutoSubmissionSettings",
      UPDATE_SOD_STATUS:"updateSodStatus",
      UPDATE_REPORTING_UNIT_WORKFLOW_SETTINGS:"varianceanalysis/reportingUnit/updateWorkflowSettings",
      UPDATE_REPORTING_UNIT_OTHER_SETTINGS:"varianceanalysis/reportingUnit/updateOtherSettings"
    }
  }


/**
 * Returns table headers for My Reporting Units page
 * @param {Function} labelResolver - Function to resolve dynamic labels (e.g., this.$nova.labelsName)
 * @returns {Array} Array of header objects
 */
export function getMyReportingUnitsTableHeaders(labelResolver) {
  return [
    {
      value: "indexno",
      text: "S.No",
      sortable: false,
      filterable: false,
      alignment: "text-center",
    },
    {
      value: "code",
      text: labelResolver('my_reporting_units_page', 'commentary_ru_code_label', 'RU code') + ' & Name',
      sortable: false,
      filterable: false,
      lock: true,
    },
    {
      value: "network",
      text: labelResolver('my_reporting_units_page', 'division_label', 'Network'),
      sortable: false,
      filterable: false,
      lock: true,
    },
    {
      value: "region",
      text: labelResolver('my_reporting_units_page', 'region_label', 'Region'),
      sortable: false,
      filterable: false,
      lock: true,
    },
    {
      value: "country",
      text: labelResolver('my_reporting_units_page', 'country_label', 'Country'),
      sortable: false,
      filterable: false,
      lock: true,
    },
    {
      value: "type",
      text: "RU Type",
      sortable: false,
      filterable: false,
      lock: true,
    },
    // {
    //   value: "sod_status",
    //   text: "SOD Status",
    //   sortable: false,
    //   filterable: true,
    //   lock: true,
    // },
    {
      value: "usersflag",
      text: "All users set?",
      sortable: false,
      lock: true,
      visible: false,
      filterable: false,
    },
    {
      value: "materialityflag_actual",
      text: "Comparative 1 Materiality set?",
      sortable: false,
      lock: true,
      visible: false,
      filterable: false,
    },
    {
      value: "materialityflag_forecast",
      text: "Comparative 2 Materiality set?",
      sortable: false,
      lock: true,
      visible: false,
      filterable: false,
    },
    // {
    //   value: "action",
    //   text: "Action",
    //   sortable: false,
    //   datatype: "action",
    //   lock: true,
    //   visible: false,
    // },
  ];
}

/**
 * Returns table headers for My Reporting Units page in View Only mode
 * @param {Function} labelResolver - Function to resolve dynamic labels (e.g., this.$nova.labelsName)
 * @returns {Array} Array of header objects
 */
export function getMyReportingUnitsViewOnlyHeaders(labelResolver) {
  return [
    {
      value: "indexno",
      text: "#",
      sortable: false,
      filterable: false,
    },
    {
      value: "code",
      text: labelResolver('my_reporting_units_page', 'unit_code_label', 'RU code'),
      sortable: true,
      filterable: false,
      lock: true,
    },
    {
      value: "name",
      text: "Name",
      sortable: true,
      filterable: false,
      lock: true,
    },
    {
      value: "network",
      text: labelResolver('my_reporting_units_page', 'division_label', 'Network'),
      sortable: true,
      filterable: false,
      lock: true,
    },
    {
      value: "region",
      text: labelResolver('my_reporting_units_page', 'region_label', 'Region'),
      sortable: true,
      filterable: false,
      lock: true,
    },
    {
      value: "country",
      text: labelResolver('my_reporting_units_page', 'country_label', 'Country'),
      sortable: true,
      filterable: false,
      lock: true,
    },
    {
      value: "role",
      text: "Role",
      sortable: true,
      filterable: false,
      lock: true,
    }
  ];
}

export const varianceAnalysisConstants = {
  idToClCodeMap: {
    "DESIGNATED": "DESTOT",
    "LOCAL": "LOCTOT",
  },
  CLIENT_SALES_BREAKDOWN: {
    RU_TYPE: {
      SOXID: 'soxid',
      RU: 'ru',
      GROUP_M: 'groupm',
    },
    CL_CODES: {
      LOCTOT: 'LOCTOT',
      DESTOT: 'DESTOT',
      CLITOT: 'CLITOT',
      TOTCLI: 'TOTCLI',
    },
    CL_TYPES: {
      LOCAL: 'LOCAL',
      DESIGNATED: 'DESIGNATED',
      OTHER: 'OTHER',
      OTHERS: 'OTHERS',
      TOTAL: 'TOTAL',
      SUBTOTAL: 'SUBTOTAL',
    },
    VIEW_MODES: {
      GROUPED: 'grouped',
      UNGROUPED: 'ungrouped',
    },
    HEADER_DATA_TYPES : {
      FINANCIAL_NUMBER: "financialNumber"
    }
  },
  STAGES: {
    STAGE_LOCAL: "stage_local",
    STAGE_REGIONAL: "stage_regional",
    COMMENTARY_AWAITING: 0,
    COMMENTARY_NIL_VALUE: 3,
    IMMATERIAL_VARIANCE: 4,
    COMMENTARY_NOT_IN_SCOPE: 5
  },
  JV_COMMENTARY: {
    ACTUAL: "jv_commentary1",
    FORECAST: "jv_commentary2"
  },
  WORKFLOWS_STATUS: {
    IS_LOCAL_ENABLED: "islocalenabled",
    IS_REGIONAL_ENABLED: "isregionalenabled",
    IS_WORKFLOW_ENABLED: "isworkflowsenabled"
  },
  ACTIONS: {
    ADD_LOCAL_COMMENTARY: "addlocal",
    ADD_REGIONAL_COMMENTARY: "addregional",
    VIEW: "view",
    APPROVE_LOCAL: "approvelocal",
    APPROVE_REGIONAL: "approveregional",
    REQUEST_LOCAL: "requestlocal",
    REQUEST_REGIONAL: "requestregional"
  }
};

export const DATA_PROCESSING = {
  CODES: {
    IMPORT_USER_SECURITY: "import_user_security",
    IMPORT_ACTUAL_CYCLE_GL_DATA: "import_actualcycle_gl_data",
    IMPORT_FORECAST_CYCLE_GL_DATA: "import_forecastcycle_gl_data",
    IMPORT_JV_ACTUAL_CYCLE_GL_DATA: "import_jv_actualcycle_gl_data",
    IMPORT_SOXID_ACTUAL_CYCLE_GL_DATA: "import_soxid_actualcycle_gl_data",
    IMPORT_USER_SECURITY_BULK_UPLOAD: "import_user_security_bulk_upload",
    IMPORT_REVENUE_DATA: "import_revenue_data",
    IMPORT_NET_SALES_DATA: "import_netsales_data",
    IMPORT_JV_FORECAST_CYCLE_GL_DATA: "import_jv_forecastcycle_gl_data",
  },
  STATUS: {
    PENDING: "Pending",
    COMPLETED: "Completed",
    FAILED: "Failed",
    DONE: "done"
  },
  CODE_NAMES: {
    "import_user_security": "User Security",
    "import_revenue_data": "Client by client - Revenue",
    "import_netsales_data": "Client by client - Net Sales",
    "import_forecastcycle_gl_data": "Forecast Data",
    "import_actualcycle_gl_data": "Actual Data",
    "import_jv_actualcycle_gl_data": "Actual JV Data",
    "import_jv_forecastcycle_gl_data": "Forecast JV Data",
    "import_soxid_actualcycle_gl_data": "SOXID Actual Data"
  },
  PROCESS_CONFIG: {
    "import_user_security": { category: "master", name: "User Security" },
    "import_user_security_bulk_upload": { category: "master", name: "User Security Bulk Upload" },
    "import_aggmarket_netsales_data": { category: "drilldown", name: "Agg. Market Netsales Data" },
    "gl_master": { category: "master", name: "GL Master" },
    "import_actualcycle_gl_data": { category: "gldashboard", name: "Actual Data" },
    "import_soxid_netsales_data": { category: "drilldown", name: "Soxid Netsales Data" },
    "import_netsales_data": { category: "drilldown", name: "Netsales Data" },
    "import_aggmarket_revenue_data": { category: "drilldown", name: "Agg. Market Revenue Data" },
    "import_jv_netsales_data": { category: "drilldown", name: "JV Netsales Data" },
    "import_revenue_data": { category: "drilldown", name: "Revenue Data" },
    "import_forecastcycle_gl_data": { category: "gldashboard", name: "Forecast Data" },
    "aggmarket_master": { category: "master", name: "Agg. Market Master" },
    "import_jv_actualcycle_gl_data": { category: "gldashboard", name: "JV Actual Data" },
    "import_jv_revenue_data": { category: "drilldown", name: "JV Revenue Data" },
    "ru_master": { category: "master", name: "RU Master" },
    "import_soxid_revenue_data": { category: "drilldown", name: "Soxid Revenue Data" },
    "import_soxid_actualcycle_gl_data": { category: "gldashboard", name: "Soxid Actual Data" },
    "import_jv_mapping_master_data": { category: "master", name: "JV Mapping Master" },
    "soxid_master": { category: "master", name: "Soxid Master" },
    "import_aggmarket_actualcycle_gl_data": { category: "gldashboard", name: "JV Aggmarket Data" },
    "import_bs_actualcycle_gl_data": { category: "gldashboard", name: "Actual Data (Balance Sheet)" },
    "import_soxid_bs_actualcycle_gl_data": { category: "gldashboard", name: "Soxid Actual Data (Balance Sheet)" },
    "import_aggmarket_bs_actualcycle_gl_data": { category: "gldashboard", name: "Agg. Market Actual Data (Balance Sheet)" },
    "client_master": { category: "master", name: "Client Data" },
    "import_trade_receivables_data": { category: "drilldown", name: "Trade Receivables Data" },
    "import_soxid_trade_receivables_data": { category: "drilldown", name: "Soxid Trade Receivables Data" },
    "import_aggmarket_trade_receivables_data": { category: "drilldown", name: "Agg. Market Trade Receivables Data" },
    "import_accrued_revenue_data": { category: "drilldown", name: "Accrued Revenue Data" },
    "import_soxid_accrued_revenue_data": { category: "drilldown", name: "Soxid Accrued Revenue Data" },
    "import_aggmarket_accrued_revenue_data": { category: "drilldown", name: "Agg. Market Accrued Revenue Data" },
    "import_deferred_revenue_data": { category: "drilldown", name: "Deferred Revenue Data" },
    "import_soxid_deferred_revenue_data": { category: "drilldown", name: "Soxid Deferred Revenue Data" },
    "import_aggmarket_deferred_revenue_data": { category: "drilldown", name: "Agg. Market Deferred Revenue Data" },
    "import_wip_data": { category: "drilldown", name: "WIP Data" },
    "import_soxid_wip_data": { category: "drilldown", name: "Soxid WIP Data" },
    "import_aggmarket_wip_data": { category: "drilldown", name: "Agg. Market WIP Data" },
  },
  UNITS: {
    USERS: "Users",
    PBT: "PBT"
  }
};



export const VARIANCE_ANALYSIS_UI = {
  NO_DATA_TITLE: 'No data available',
  NO_DATA_DESCRIPTION: 'Try adjusting your filters or syncing to refresh the results.',
  NO_DATA_ICON: 'mdi-database-off',
  NO_DATA_ICON_SIZE: 56,
  NO_FILTER_RESULTS_ICON: 'mdi-filter-off-outline',
  NO_FILTER_RESULTS_ICON_SIZE: 48,
  NO_FILTER_RESULTS_HINT: 'Try adjusting your filter options above',
  FILTER_NO_DATA_TITLE: 'No data available for selected filters',
  FILTER_NO_DATA_DESCRIPTION: "You don't have access to the data for the specific scenario.",
  FILTER_NO_DATA_CTA: 'Reset View',
  NO_FILTER_RESULTS_DESCRIPTION: 'No reporting units match your current filters.',
  NO_APPROVAL_COMMENT: 'No approval comment available.',
};


export const FAVOURITE_FILTERS = {
  MAX_LIMIT: 5,
  API_ENDPOINTS: {
    BASE: '/v3/varianceanalysis/analysis/getfavouritefilters',
    BASE_CREATE: '/v3/varianceanalysis/analysis/favouritefilters',
    BASE_DELETE: '/v3/varianceanalysis/analysis/favouritefilters',
  },
  MESSAGES: {
    FETCH_SUCCESS: 'Favourite filters loaded.',
    FETCH_ERROR: 'Failed to load favourite filters.',
    CREATE_SUCCESS: 'Filter saved to favourites.',
    CREATE_ERROR: 'Failed to save favourite filter.',
    DELETE_SUCCESS: 'Favourite filter removed.',
    DELETE_ERROR: 'Failed to remove favourite filter.',
    CLIENT_REQUIRED: 'Client information is required to manage favourite filters.',
    TITLE_REQUIRED: 'Title is required.',
  },
  UI: {
    INFO_TEXT: 'You can save up to 5 filters.\nSaving the 6th filter will remove the oldest one.',
    CONFIRM_REPLACE_MESSAGE: 'Saving this new filter will remove your oldest filter:',
    CONFIRM_CANCEL_CTA: 'Cancel',
    CONFIRM_SAVE_CTA: 'Continue & Save',
    EMPTY_STATE: 'No favourite filters yet.',
    DIALOG_TITLE: 'Save Favourites',
    TITLE_LABEL: 'Add title',
    TITLE_PLACEHOLDER: 'Add title',
    SAVE_CTA: 'Save',
    RECENT_SECTION_TITLE: 'Recent filters',
    UNTITLED_LABEL: 'Untitled filter',
    SUMMARY_FIELDS: [
      { key: 'year', label: 'Year' },
      { key: 'period', label: 'Period' },
      { key: 'type', label: 'Type' },
      { key: 'network', label: 'Network' },
      { key: 'region', label: 'Region' },
      { key: 'country', label: 'Country' },
      { key: 'reportingunit', label: 'Reporting Unit' },
    ],
    SUMMARY_SEPARATOR: ' • ',
    SUMMARY_FALLBACK: 'All filters',
    VALUE_ALL_LABEL: 'All',
  },
};




export const BULK_UPLOAD_TABS = [
  { label: 'RU', value: 'rufile' },
  { label: 'GL', value: 'glfile' },
  { label: 'Drill-Down', value: 'slfile' },
];

export const STATEMENT_OPTIONS = [
  { labelName: 'Profit & Loss', name: 'pl' },
  { labelName: 'Balance Sheet', name: 'bs' }
];

export const RU_FILE_COLUMNS_ACTUAL = ['rucode', 'runame', 'month', 'year', 'a_c_l_amt_pro', 'a_c_l_per_pro', 'a_m_l_amt_pro', 'a_m_l_per_pro', 'a_c_r_amt_pro', 'a_c_r_per_pro', 'a_m_r_amt_pro', 'a_m_r_per_pro', 'a_c_l_amt_fore', 'a_c_l_per_fore', 'a_m_l_amt_fore', 'a_m_l_per_fore', 'a_c_r_amt_fore', 'a_c_r_per_fore', 'a_m_r_amt_fore', 'a_m_r_per_fore'];

export const RU_FILE_COLUMNS_FORECAST = ['rucode', 'runame', 'month', 'year', 'f_c_l_amt_pro', 'f_c_l_per_pro', 'f_m_l_amt_pro', 'f_m_l_per_pro', 'f_c_r_amt_pro', 'f_c_r_per_pro', 'f_m_r_amt_pro', 'f_m_r_per_pro', 'f_c_l_amt_fore', 'f_c_l_per_fore', 'f_m_l_amt_fore', 'f_m_l_per_fore', 'f_c_r_amt_fore', 'f_c_r_per_fore', 'f_m_r_amt_fore', 'f_m_r_per_fore'];

export const GL_FILE_COLUMNS_ACTUAL = ['rucode', 'runame', 'glcode', 'glname', 'month', 'year', 'a_c_l_amt_pro', 'a_c_l_per_pro', 'a_m_l_amt_pro', 'a_m_l_per_pro', 'a_c_r_amt_pro', 'a_c_r_per_pro', 'a_m_r_amt_pro', 'a_m_r_per_pro', 'a_c_l_amt_fore', 'a_c_l_per_fore', 'a_m_l_amt_fore', 'a_m_l_per_fore', 'a_c_r_amt_fore', 'a_c_r_per_fore', 'a_m_r_amt_fore', 'a_m_r_per_fore'];

export const GL_FILE_COLUMNS_FORECAST = ['rucode', 'runame', 'glcode', 'glname', 'month', 'year', 'f_c_l_amt_pro', 'f_c_l_per_pro', 'f_m_l_amt_pro', 'f_m_l_per_pro', 'f_c_r_amt_pro', 'f_c_r_per_pro', 'f_m_r_amt_pro', 'f_m_r_per_pro', 'f_c_l_amt_fore', 'f_c_l_per_fore', 'f_m_l_amt_fore', 'f_m_l_per_fore', 'f_c_r_amt_fore', 'f_c_r_per_fore', 'f_m_r_amt_fore', 'f_m_r_per_fore'];

export const SL_FILE_COLUMNS_ACTUAL = ['rucode', 'runame', 'glcode', 'glname', 'month', 'year', 'a_c_l_amt_pro', 'a_c_l_per_pro', 'a_m_l_amt_pro', 'a_m_l_per_pro', 'a_c_r_amt_pro', 'a_c_r_per_pro', 'a_m_r_amt_pro', 'a_m_r_per_pro', 'a_c_l_amt_fore', 'a_c_l_per_fore', 'a_m_l_amt_fore', 'a_m_l_per_fore', 'a_c_r_amt_fore', 'a_c_r_per_fore', 'a_m_r_amt_fore', 'a_m_r_per_fore'];

export const SL_FILE_COLUMNS_FORECAST = ['rucode', 'runame', 'glcode', 'glname', 'month', 'year', 'f_c_l_amt_pro', 'f_c_l_per_pro', 'f_m_l_amt_pro', 'f_m_l_per_pro', 'f_c_r_amt_pro', 'f_c_r_per_pro', 'f_m_r_amt_pro', 'f_m_r_per_pro', 'f_c_l_amt_fore', 'f_c_l_per_fore', 'f_m_l_amt_fore', 'f_m_l_per_fore', 'f_c_r_amt_fore', 'f_c_r_per_fore', 'f_m_r_amt_fore', 'f_m_r_per_fore'];


export const ITEMS_PER_PAGE = 25;

export const REPORTS_API = {
    DOWNLOAD_ALL_RU_EXCEL_LIST: '/v3/varianceanalysis/analysis/downloadallruexcellist/',
    DOWNLOAD_ALL_RU_EXCEL: '/v3/varianceanalysis/analysis/downloadallruexcel/',
    DOWNLOAD: '/v3/varianceanalysis/analysis/download/',
    DOWNLOAD_ALL_ACCESS_EXCEL_LIST: '/v3/varianceanalysis/reportingunit/downloadallaccessexcellist',
    DOWNLOAD_ALL_ACCESS_EXCEL: '/v3/varianceanalysis/reportingunit/downloadallaccessexcel/',
    DOWNLOAD_ALL_MATERIALITY_EXCEL_LIST: '/v3/varianceanalysis/reportingunit/downloadallRUMaterialityexcellist',
    DOWNLOAD_ALL_MATERIALITY_EXCEL: '/v3/varianceanalysis/reportingunit/downloadallRUMaterialityexcel',
    DOWNLOAD_ALL_APPROVALS_LIST: '/v3/varianceanalysis/analysis/getapprovalreportlist',
    DOWNLOAD_APPROVAL_REPORT: '/v3/varianceanalysis/analysis/downloadapprovalreport',
    DATA_CENTRE_REPORT_LIST: '/v3/varianceanalysis/analysis/getdatacentrreportlist',
    DOWNLOAD_DATA_CENTRE_REPORT: '/v3/varianceanalysis/analysis/downloaddatacentrreport'
};

export const SOD = {
  STATUS: {
    ENABLED: "Enabled",
    DISABLED: "Disabled"
  },
  MESSAGES: {
    SOD_MIXED_WARNING: "Please note that for ReportingUnit - RU, there's a conflict of Segregation of Duties (SOD). This means that for some users, SOD is enabled, while for others, it is disabled."
  }
};


export const REPORTING_TYPES = {
  PL: 'pl',
  BS: 'bs',
  PL_LABEL: 'Profit & Loss',
  BS_LABEL: 'Balance Sheet'
};

export const SORT_ORDER = {
  ASC: 'asc',
  DESC: 'desc'
};

export const PERIOD_TYPE = {
  MTD: 'mtd',
  YTD: 'ytd'
};

export const AMOUNT_TYPES = {
  ACTUAL_AMOUNT: 'Actual Amount',
  PROFORMA_AMOUNT: 'Proforma Amount',
  FORECAST_AMOUNT: 'Forecast Amount'
};

/**
 * Queue Management API endpoints
 */
export const QUEUE_MANAGEMENT_ENDPOINTS = {
  PEEK: '/v3/varianceanalysis/queuemanagement/peek',
  SEND: '/v3/varianceanalysis/queuemanagement/send',
  SILKROUTE_TASK_STATE: '/v3/varianceanalysis/queuemanagement/silkroute_task_state',
  SILKROUTE_FLOW_STATE: '/v3/varianceanalysis/queuemanagement/silkroute_flow_state',
};

/**
 * Queue Management flow collection types
 */
export const FLOW_COLLECTION_TYPES = {
  TASK_STATE: 'silkroute_task_state',
  FLOW_STATE: 'silkroute_flow_state',
};

export default {
  SORT_ORDER,
  PERIOD_TYPE,
  REPORTING_TYPES,
  PAGINATION,
  MATERIALITY_TYPE_LABELS,
  NOTIFICATIONS,
  JV_COMMENTARY,
  STAGES,
  WORKFLOWS_STATUS,
  AMOUNT_TYPES,
  QUEUE_MANAGEMENT_ENDPOINTS,
  FLOW_COLLECTION_TYPES,
};
